import { RestrictedCdnSignature } from "@iventis/api-helpers";
import { transformRequest } from "@iventis/map-engine/src/bridge/mapbox/tile-requests";
import { InfoTooltip, VerticalToolbar } from "@iventis/components";
import { WarningMessageComponent } from "@iventis/components/src/warning-message";
import { userLocalStorageItems, userLocalStorageManager } from "@iventis/local-storage/src/user-local-storage";
import {
    AssetOptions,
    EventMessageMap,
    MAPBOX_MAX_ZOOM,
    MAPBOX_MIN_ZOOM,
    MapCursor,
    MapState,
    MapboxEngine,
    StoredPosition,
    TileSource,
    mapFontStackUrl,
    setMapObjectTilesZoomOptimisation,
    machineEventTypes,
} from "@iventis/map-engine";
import { InfoPanelButton, LockPanelButton, ZoomInPanelButton, ZoomOutPanelButton, mapcontrollbuttonStyle } from "@iventis/map-engine-controls";
import { ExportOptions } from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox-types-and-constants";
import { getSitemapLevelsWithinBoundsAndLevelIndex } from "@iventis/map-engine/src/utilities/sitemap-helpers";
import { editStyleFormGap, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Header3 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { queryParams, EventStream, xstateDevTools, mapboxKey, useResizeObserver, API_KEY_QUERY_PARAM_KEY, PROJECT_ID_QUERY_PARAM_KEY } from "@iventis/utilities";
import { ExportFormat } from "@iventis/domain-model/model/exportFormat";
import { PageOrientation } from "@iventis/domain-model/model/pageOrientation";
import { MapExportLegendPlacement } from "@iventis/domain-model/model/mapExportLegendPlacement";
import { MapExportApiCreateRequest } from "@iventis/domain-model/model/mapExportApiCreateRequest";
import { Node } from "@iventis/domain-model/model/node";
import React, { useRef, useEffect, forwardRef, useImperativeHandle, Ref, useState, useMemo, useLayoutEffect } from "react";
import { BehaviorSubject } from "rxjs";
import { useTheme } from "@mui/material";
import { v4 as uuid } from "uuid";
import { Model } from "@iventis/domain-model/model/model";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { MapStoreEvent, MapStoreEventType } from "@iventis/map-engine/src/types/events-store";
import { SelectedFormOption } from "./export-map-dialog";
import { getFlexDirection, getLegendDimensions, getMapHeightAndWidth, filterMapObjectsForMapExport, filterLayersForMapExport, getLayerVisibilityForExport } from "./export-helpers";
import { LegendComponent } from "./legend-component";
import { PositionMapOverlay } from "./position-map-overlay";

export interface MapPreviewProps {
    signatures: RestrictedCdnSignature[];
    mapApiFunctions: AssetOptions & {
        mapTilesGetter: () => TileSource[];
        exportMap: (options: MapExportApiCreateRequest, mapId: string) => Promise<void>;
        getAttributeListItems: (attributeId: string) => Promise<DataFieldListItem[]>;
    };
    initialState: MapState;
    getMapStartingPosition: () => ExportOptions;
    exportFormValues: SelectedFormOption;
    map: Node;
    userFirstName: string;
    userLastName: string;
    projectName: string;
    projectLogo: string;
    ref?: Ref<MapPreviewRef>;
    selectedSitemapVersions: SitemapVersion[];
    zoomChanged: () => void;
    pitchChanged: () => void;
    layersWithTemporaryVisibilityValue: string[];
    userId: string;
}

export type MapPreviewRef = {
    getExportPosition: () => ExportOptions;
    getModels: () => Model[];
    setBounds: (bounds: [number, number, number, number]) => Promise<void>;
    getPosition: () => Promise<StoredPosition>;
    setBearing: (number) => void;
    getSitemapsLevelName: () => string;
};

export const MapPreview: React.FunctionComponent<MapPreviewProps> = forwardRef<MapPreviewRef, MapPreviewProps>(
    (
        {
            mapApiFunctions,
            initialState,
            signatures,
            getMapStartingPosition,
            exportFormValues,
            map,
            projectLogo,
            projectName,
            userFirstName,
            userLastName,
            selectedSitemapVersions,
            zoomChanged,
            pitchChanged,
            layersWithTemporaryVisibilityValue,
            userId,
        },
        ref
    ) => {
        const getSitemapLevelNames = (sitemapLevels: SitemapVersionLevel[]): string => {
            if (sitemapLevels.length === 0) {
                return null;
            }
            const levelNames = [...new Set(sitemapLevels.map((level) => level.levelName))];
            if (levelNames.length === 1) {
                return levelNames[0];
            }
            return [...new Set(sitemapLevels.map((level) => level.levelAbbreviation))].sort().join(" / ");
        };
        // Map and store setup
        const [sitemapsLevelName, updateSitemapsLevelName] = useState<string>(
            getSitemapLevelNames(getSitemapLevelsWithinBoundsAndLevelIndex(initialState.bounds.value.bounds, selectedSitemapVersions, initialState.currentLevel))
        );

        const [bearing, setBearing] = useState<number>(initialState.position.value.bearing);

        const storeRef = useRef<BehaviorSubject<MapState>>();
        const eventStreamRef = useRef<EventStream<EventMessageMap>>(null);
        const mapElementRef = useRef<HTMLDivElement>();
        const mapEngine = useRef<MapboxEngine>();
        const currentZoom = useRef<number>(initialState.position.value.zoom);
        const currentPitch = useRef<number>(initialState.position.value.pitch);

        useResizeObserver(mapElementRef, () => {
            mapEngine.current?.onResize();
        });

        const [mapContainerHeight, setMapContainerHeight] = useState<number>(0);
        const mapDimensionsRef = useRef<{ height: number; width: number }>({ height: 0, width: 0 });
        const modelsRef = useRef<Model[]>([]);
        const mapContainerRef = useRef<HTMLDivElement>();

        useLayoutEffect(() => {
            const initialHeight = mapContainerRef.current?.clientHeight ?? 0;
            if (initialHeight > 0) {
                setMapContainerHeight(initialHeight);
            }
        }, []);

        const translate = useIventisTranslate();
        const theme = useTheme<Theme>();

        const [mapLocked, setMapLocked] = useState(false);
        const [showInfoTooltip, setShowInfoTooltip] = useState(false);
        const [showPositionMapOverlay, setShowPositionMapOverlay] = useState(
            () => userLocalStorageManager.get(userLocalStorageItems.exportPreviewPositionMapOverlay, userId) !== true
        );

        const hidePositionMapOverlay = () => {
            setShowPositionMapOverlay(false);
        };

        const dismissPositionMapOverlay = () => {
            setShowPositionMapOverlay(false);
            userLocalStorageManager.set(userLocalStorageItems.exportPreviewPositionMapOverlay, userId, true);
        };

        const showLegend = exportFormValues.fileFormat === ExportFormat.Pdf && exportFormValues.legendPosition !== MapExportLegendPlacement.Hidden;

        const showVisibilityWarning = layersWithTemporaryVisibilityValue.length > 0;

        const modifyStore = (change: Partial<MapState>) => {
            storeRef.current.next({
                ...storeRef.current.value,
                ...change,
            });
        };

        useImperativeHandle(
            ref,
            () => ({
                getExportPosition: () => mapEngine.current.getExportPosition(),
                getModels: () => modelsRef.current,
                setBounds: async (bounds: [number, number, number, number]) => {
                    mapEngine.current.setBoundsWithBounds(bounds, 0, 24, 0);
                    // Update the current zoom, so that the zoom change is not detected as a change when the new bounds are set
                    currentZoom.current = (await mapEngine.current.getPosition()).zoom;
                    currentPitch.current = (await mapEngine.current.getPosition()).pitch;
                },
                getPosition: () => mapEngine.current.getPosition(),
                setBearing: (bearing: number) => mapEngine.current.setBearing(bearing),
                getSitemapsLevelName: () => sitemapsLevelName,
            }),
            []
        );

        const handleMapLock = () => {
            setMapLocked(!mapLocked);
            modifyStore({
                isCameraMovementLocked: { value: !mapLocked, stamp: uuid() },
                overrideCursor: { value: mapLocked ? MapCursor.READ : MapCursor.DEFAULT, stamp: uuid() },
            });
        };

        // eslint-disable-next-line consistent-return
        useEffect(() => {
            if (mapElementRef.current !== undefined) {
                // Bug 15465 - Text-based map objects will sometimes not appear on PDF export
                // Always set ignoreZoomOptimisation to true to mimic the export behaviour
                const updatedTileUrl = setMapObjectTilesZoomOptimisation(initialState, "objects", true);
                const exportLayers = initialState.layers.value.map((layer) => ({
                    ...layer,
                    visible: getLayerVisibilityForExport(layer, layersWithTemporaryVisibilityValue),
                }));
                storeRef.current = new BehaviorSubject({
                    ...initialState,
                    layers: {
                        value: filterLayersForMapExport(exportLayers),
                        stamp: uuid(),
                    },
                    geoJSON: {
                        value: filterMapObjectsForMapExport(initialState.geoJSON.value),
                        stamp: uuid(),
                    },
                    overrideCursor: { value: mapLocked ? MapCursor.DEFAULT : MapCursor.READ, stamp: uuid() },
                    mapObjectsSelected: { value: [], stamp: uuid() },
                    isCameraMovementLocked: { value: mapLocked, stamp: uuid() },
                    tileSources: {
                        value: {
                            objects: { ...initialState.tileSources.value.objects, tiles: [{ ...initialState.tileSources.value.objects.tiles[0], tiles: [updatedTileUrl] }] },
                            comments: { bounds: null, tiles: initialState.tileSources.value.comments.tiles },
                        },
                        stamp: uuid(),
                    },
                    selectedSitemapVersionLevels: {
                        value: selectedSitemapVersions.flatMap((sitemapVersion) =>
                            sitemapVersion.sitemapVersionLevels.flatMap((svl) => (svl.levelIndex === initialState.currentLevel ? { id: svl.id, type: sitemapVersion.type } : []))
                        ),
                        stamp: uuid(),
                    },
                });
                eventStreamRef.current = new EventStream<EventMessageMap>();
                const startingPos = getMapStartingPosition();
                const engine = new MapboxEngine({
                    store: { change: storeRef.current },
                    container: mapElementRef.current,
                    eventStream: eventStreamRef.current,
                    mbxAccessToken: mapboxKey,
                    preview: false,
                    cypressWindowName: "IVENTIS_EXPORT_PREVIEW_ENGINE_TESTING_FUNCTIONS",
                    modifierKeys: ["Control", "Meta"],
                    assetOptions: {
                        ...mapApiFunctions,
                        multipleModelsGetter: async (ids) => {
                            const response = await mapApiFunctions.multipleModelsGetter(ids);
                            modelsRef.current = [...modelsRef.current, ...response];
                            return response;
                        },
                    },
                    transformRequest: (url) =>
                        transformRequest(
                            url,
                            signatures,
                            queryParams[API_KEY_QUERY_PARAM_KEY],
                            storeRef.current.getValue().terrain3D.value.customTerrains,
                            queryParams[PROJECT_ID_QUERY_PARAM_KEY]
                        ),
                    maxZoom: MAPBOX_MAX_ZOOM,
                    minZoom: MAPBOX_MIN_ZOOM,
                    fontStackUrl: mapFontStackUrl,
                    devTools: xstateDevTools,
                    pitchOptions: { min: 0, max: 60 },
                    exportOptions: { bounds: startingPos?.bounds, bearing: startingPos?.bearing, pitch: startingPos?.pitch },
                    getAttributeListItems: mapApiFunctions.getAttributeListItems,
                    routeApiFunctions: undefined,
                    isExternalUser: false,
                    user: { id: "", isMobileUser: false },
                    setWebGLSupported: null,
                });

                mapEngine.current = engine;
                const mapEngineSub = mapEngine.current.storeEvents.subscribe((event: MapStoreEvent) => {
                    if (event.eventType === MapStoreEventType.SET_POSITION) {
                        updateSitemapsLevelName(
                            getSitemapLevelNames(
                                getSitemapLevelsWithinBoundsAndLevelIndex(event.payload.mapPostion.bounds.bounds, selectedSitemapVersions, initialState.currentLevel)
                            )
                        );
                        setBearing(event.payload.mapPostion.position.bearing);
                        if (currentZoom.current !== event.payload.mapPostion.position.zoom) {
                            currentZoom.current = event.payload.mapPostion.position.zoom;
                            zoomChanged();
                        }
                        if (currentPitch.current !== event.payload.mapPostion.position.pitch) {
                            currentPitch.current = event.payload.mapPostion.position.pitch;
                            pitchChanged();
                        }
                    }
                });

                return () => {
                    engine.destroy();
                    storeRef.current.unsubscribe();
                    eventStreamRef.current.destroy();
                    mapEngineSub.unsubscribe();
                };
            }
        }, [mapElementRef.current]);

        const onTooltipClose = () => {
            setShowInfoTooltip(false);
        };

        const mapDimensions = useMemo(() => {
            const nextMapDimensions = getMapHeightAndWidth(
                exportFormValues?.pageOrientation,
                exportFormValues.pageSize,
                exportFormValues?.fileFormat === ExportFormat.Pdf && exportFormValues.legendPosition !== MapExportLegendPlacement.Hidden,
                mapContainerHeight
            );

            if (nextMapDimensions.width > 0 && nextMapDimensions.height > 0) {
                mapDimensionsRef.current = nextMapDimensions;
                return nextMapDimensions;
            }

            return mapDimensionsRef.current;
        }, [mapContainerHeight, exportFormValues?.pageOrientation, exportFormValues?.pageSize, exportFormValues?.fileFormat, exportFormValues.legendPosition]);

        const legendDimensions = getLegendDimensions(exportFormValues.pageSize, exportFormValues.pageOrientation, mapDimensions.width, mapDimensions.height);

        return (
            <MapExportContainer flexDirection={getFlexDirection(exportFormValues?.legendPosition)}>
                <HeaderRow>
                    <Header3>{translate(Content.map5.export_map.export_preview)}</Header3>
                    {showVisibilityWarning && (
                        <div data-testid="read-user-visibility-warning">
                            <StyledWarningMessage message={translate(Content.map5.export_map.read_user_visibility_warning)} />
                        </div>
                    )}
                </HeaderRow>
                <div className="export-preview-container" ref={mapContainerRef}>
                    {showLegend && (
                        <MapLegend dimensions={legendDimensions}>
                            <LegendComponent
                                orientation={exportFormValues.pageOrientation}
                                pageSize={exportFormValues.pageSize}
                                mapName={exportFormValues.title === "" ? map.name : exportFormValues.title}
                                projectLogo={projectLogo}
                                projectName={projectName}
                                userName={`${userFirstName} ${userLastName}`}
                                remarks={exportFormValues.remarks}
                                sitemapsLevelName={sitemapsLevelName}
                                bearing={bearing}
                                scale={exportFormValues.scale}
                            />
                        </MapLegend>
                    )}
                    <StyledExportPreview orientation={exportFormValues?.pageOrientation} mapDimensions={mapDimensions}>
                        <StyledMap data-testid="export-preview-map" ref={mapElementRef} onPointerDown={hidePositionMapOverlay} onWheel={hidePositionMapOverlay} />
                        {showPositionMapOverlay && (
                            <PositionMapOverlay
                                title={translate(Content.map5.export_map.positionMapOverlay.title)}
                                text={translate(Content.map5.export_map.positionMapOverlay.text)}
                                onClose={dismissPositionMapOverlay}
                            />
                        )}
                    </StyledExportPreview>
                </div>
                <VerticalToolbar height="auto">
                    <InfoTooltip
                        text={translate(Content.map5.export_map.infoTooltip.text)}
                        placement="left"
                        title={translate(Content.map5.export_map.infoTooltip.title)}
                        open={showInfoTooltip}
                        onClose={onTooltipClose}
                    >
                        <span style={{ position: "relative" }}>
                            <InfoPanelButton selected={showInfoTooltip} style={mapcontrollbuttonStyle(theme)} action={() => setShowInfoTooltip(!showInfoTooltip)} />
                        </span>
                    </InfoTooltip>

                    <LockPanelButton style={mapcontrollbuttonStyle(theme)} action={handleMapLock} selected={mapLocked} />
                    <ZoomInPanelButton
                        style={mapcontrollbuttonStyle(theme)}
                        action={() => eventStreamRef.current.next({ type: machineEventTypes.zoomIn })}
                        selected={false}
                        disabled={mapLocked}
                    />
                    <ZoomOutPanelButton
                        style={mapcontrollbuttonStyle(theme)}
                        action={() => eventStreamRef.current.next({ type: machineEventTypes.zoomOut })}
                        selected={false}
                        disabled={mapLocked}
                    />
                </VerticalToolbar>
            </MapExportContainer>
        );
    }
);

const MapExportContainer = styled.div<{ flexDirection: "row" | "column" | "column-reverse" | "row-reverse" }>`
    display: flex;
    flex-direction: column;
    gap: ${editStyleFormGap};
    width: 100%;
    height: 98%;
    position: relative;

    .export-preview-container {
        display: flex;
        flex-direction: ${(props) => props.flexDirection};
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        min-height: 250px;
        background-color: ${({ theme }: { theme: Theme }) => theme.shades.lightBorder};

        .mapboxgl-ctrl-bottom-right {
            margin-right: 0 !important;
            right: 0;
        }
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const StyledWarningMessage = styled(WarningMessageComponent)`
    margin-left: auto;
    max-width: 70%;
`;

const StyledMap = styled.div`
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.two};
    box-sizing: content-box;
    width: 100%;
    height: 100%;
`;

const StyledExportPreview = styled.div<{ orientation: PageOrientation; mapDimensions: { width: number; height: number } }>`
    position: relative;
    width: ${(props) => props.mapDimensions.width}px;
    height: ${(props) => props.mapDimensions.height}px;
`;

const MapLegend = styled.div<{ dimensions: { height: number; width: number } }>`
    height: ${(props) => props.dimensions.height}px;
    width: ${(props) => props.dimensions.width}px;
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
`;
